import type { GraphQLFormattedError } from 'graphql';

/**
 * TIC-7021: the create/edit doors refuse a non-service user who carries no shift, with
 * `RequiredFieldInputError` under the keyable code `USER_SHIFT_REQUIRED`.
 */
export const USER_SHIFT_REQUIRED_CODE = 'USER_SHIFT_REQUIRED';

/**
 * What the two user forms say about that refusal, in their own words.
 *
 * The server's sentence is pinned verbatim by a backend contract test, but it speaks the API's
 * vocabulary rather than this form's — the form says the refusal in its own words instead.
 */
export const USER_SHIFT_REQUIRED_TEXT =
  'This person needs a shift. Pick one under Shift and save again — ' +
  'everyone but a service account works a shift.';

/**
 * What the forms say where the write never reached the server.
 *
 * Needed because these two operations carry `skipGraphqlErrorUi`, which takes them out of the
 * middleware's network-error toast as well — without this line a dropped connection on save would
 * say nothing at all.
 */
export const USER_WRITE_FAILED_TEXT =
  'Saving failed before it reached the server. Check your connection and try again.';

/** A write as the two user pages read it: Apollo's result shape stops at the service. */
export type UserWriteResult = {
  ok: boolean;
  /** The lines the form renders. Empty where the write went through. */
  errors: string[];
};

const errorCode = (error: GraphQLFormattedError): string | undefined =>
  (error.extensions as { code?: string } | undefined)?.code;

/**
 * The own-words sentence for a refusal this screen recognises, or `null` to fall through.
 *
 * Read off `extensions.code` and **never** off the message: the code is the API's stable key for
 * the refusal class, while the prose behind it belongs to the server and changes without notice
 * (the agent-date doctrine).
 */
export const userWriteRefusalText = (
  errors: readonly GraphQLFormattedError[] | null | undefined,
): string | null =>
  (errors ?? []).some((error) => errorCode(error) === USER_SHIFT_REQUIRED_CODE)
    ? USER_SHIFT_REQUIRED_TEXT
    : null;

/**
 * One mutation answer as the form reads it.
 *
 * The recognised refusal is said in our own words; anything else keeps the server's own messages,
 * which is what the global toast would have shown — the operator is never left without the text.
 */
export const toUserWriteResult = (
  errors: readonly GraphQLFormattedError[] | null | undefined,
  ok: boolean | null | undefined,
): UserWriteResult => {
  const refusal = userWriteRefusalText(errors);

  if (refusal) {
    return { ok: false, errors: [refusal] };
  }

  return {
    ok: !!ok,
    errors: (errors ?? []).map(({ message }) => message),
  };
};
